import type { ValidationError } from '../../vendors/errors/validation-error'

import { Col, Form, Row, Select, Typography } from 'antd'
import { format } from 'date-fns'

import {
  InvoiceProcessingLevel,
  mapper as invoiceProcessingMapper
} from '../../../domain/invoice-processing-level.enum'
import { mapper as pushTypeNameMapper, PushType } from '../../../domain/push-type.enum'
import { ordinal } from '../formatters'

interface FundingInformationProps {
  validationError?: Partial<ValidationError>
  fundingType?: PushType
  nextPullDate?: Date
  formattedNextPullDate?: string
}

export function FundingInformation({
  validationError,
  fundingType,
  nextPullDate,
  formattedNextPullDate
}: FundingInformationProps): JSX.Element {
  return (
    <Row className='border p-4 rounded-lg mb-4'>
      <Col className='w-full'>
        <Typography.Title
          level={5}
          className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray mb-2'
        >
          Funding Information
        </Typography.Title>
        <Form.Item
          name='premium_funding_type'
          label={
            <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
              Funding Type
            </span>
          }
          validateStatus={validationError?.issues?.premium_funding_type ? 'error' : undefined}
          help={validationError?.issues?.premium_funding_type}
          tooltip={
            <div className='flex flex-col gap-2'>
              <p>
                <strong>Push</strong> is when groups send us money to fund their claims via ACH,
                wire or physical check.
              </p>
              <p>
                <strong>Pull</strong> is when we go get the money from the group&apos;s local bank
                by ACH.
              </p>
              <p>
                <strong>FBB</strong> — First Business Bank.
              </p>
            </div>
          }
          required
        >
          <Select
            size='middle'
            className='w-full rounded-md'
            placeholder='Select Funding Type'
            allowClear
            options={[
              {
                label: pushTypeNameMapper[PushType.Push],
                value: PushType.Push
              },
              {
                label: pushTypeNameMapper[PushType.Pull],
                value: PushType.Pull
              },
              {
                label: pushTypeNameMapper[PushType.FBB],
                value: PushType.FBB
              }
            ]}
          />
        </Form.Item>

        <Form.Item
          name='premium_schedule_day'
          label={
            <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
              Schedule Day
            </span>
          }
          validateStatus={validationError?.issues?.premium_schedule_day ? 'error' : undefined}
          help={
            validationError?.issues?.premium_schedule_day ??
            (fundingType === PushType.Pull && nextPullDate && formattedNextPullDate ? (
              <p className='text-right'>
                Next pull date is{' '}
                <span title={format(nextPullDate, 'EEEE')}>{formattedNextPullDate}</span>
              </p>
            ) : undefined)
          }
          required={fundingType === PushType.Pull}
        >
          <Select
            size='large'
            className='w-full rounded-md'
            placeholder={fundingType === PushType.Pull ? 'Select Schedule Day' : '—'}
            disabled={fundingType !== PushType.Pull}
            allowClear
            options={Array.from({ length: 31 }, (_, k) => k + 1).map(day => {
              return {
                label: ordinal(day),
                value: day
              }
            })}
          />
        </Form.Item>
        <Form.Item
          name='invoice_processing_level'
          label={
            <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
              Invoice Processing Level
            </span>
          }
          validateStatus={validationError?.issues?.invoice_processing_level ? 'error' : undefined}
          help={validationError?.issues?.invoice_processing_level}
          tooltip={
            <div className='flex flex-col gap-2'>
              <p>
                <strong>Group Leve</strong> - all invoices are linked to a single group account
              </p>
              <p>
                <strong>Invoice Division Level</strong> - invoices are linked to different group
                accounts
              </p>
            </div>
          }
          required
        >
          <Select
            size='middle'
            className='w-full rounded-md'
            placeholder={fundingType === PushType.Pull ? 'Select Invoie Level' : '—'}
            allowClear
            disabled={fundingType !== PushType.Pull}
            options={[
              {
                label: invoiceProcessingMapper[InvoiceProcessingLevel.Group],
                value: InvoiceProcessingLevel.Group
              },
              {
                label: invoiceProcessingMapper[InvoiceProcessingLevel.Division],
                value: InvoiceProcessingLevel.Division
              }
            ]}
          />
        </Form.Item>
      </Col>
    </Row>
  )
}
